import type { DateSpec, SnoozeInput } from '@taskos/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { errorMessage, post } from '../../api/client';
import { invalidateTasks, useSnoozeOptions } from '../../api/queries';
import { useUI } from '../../app/ui';
import { useToast } from '../../components/toast';
import { Button, ConfirmDialog, Field, Modal, Spinner } from '../../components/ui';
import { formatDateTime, formatTimeOf, todayISO, zonedToISO } from '../../lib/format';
import { useTaskActions } from './actions';
import { DatePicker } from './DatePicker';

/** ⏰ Snooze picker with presets + custom time. */
export function SnoozeDialog() {
  const ui = useUI();
  const t = ui.snoozeFor;
  const options = useSnoozeOptions(!!t);
  const actions = useTaskActions();
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState('18:00');
  if (!t) return null;
  const pick = async (input: SnoozeInput) => {
    const r = await actions.snooze(t, input);
    if (r) ui.closeSnooze();
  };
  return (
    <Modal open onClose={ui.closeSnooze} title="⏰ Snooze" size="narrow">
      <p className="muted small mb">{t.title}</p>
      {options.isPending ? (
        <Spinner />
      ) : (
        <div className="date-grid" role="list">
          {(options.data ?? []).map((o) => (
            <button key={o.id} type="button" className="date-btn" role="listitem" disabled={!o.available} onClick={() => pick({ preset: o.id as never })} title={o.available ? undefined : 'Not available right now'}>
              <strong>{o.label}</strong>
              <span>{o.until ? (o.id.endsWith('m') || o.id === '1h' ? formatTimeOf(o.until) : formatDateTime(o.until)) : 'Not available'}</span>
            </button>
          ))}
        </div>
      )}
      <div className="divider" />
      <div className="inline-row">
        <label className="small" htmlFor="snooze-date">
          Custom
        </label>
        <input id="snooze-date" type="date" className="input" style={{ width: 160 }} value={date} min={todayISO()} onChange={(e) => setDate(e.target.value)} />
        <input type="time" className="input" style={{ width: 120 }} value={time} onChange={(e) => setTime(e.target.value)} aria-label="Custom snooze time" />
        <Button variant="primary" disabled={!date || !time} onClick={() => pick({ preset: 'custom', until: zonedToISO(date, time) })}>
          Snooze
        </Button>
      </div>
    </Modal>
  );
}

/** Snooze-all with explicit confirmation (spec: must confirm). */
export function SnoozeAllButton() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  return (
    <>
      <Button onClick={() => setOpen(true)}>😴 Snooze all</Button>
      <ConfirmDialog
        open={open}
        title="Snooze all current notifications?"
        message="All overdue and due-today tasks assigned to you will be snoozed for 1 hour."
        confirmLabel="SNOOZE ALL"
        onCancel={() => setOpen(false)}
        onConfirm={async () => {
          try {
            const r = await post<{ snoozed: number }>('/api/tasks/snooze-all', { preset: '1h' });
            toast.push({ kind: 'success', title: `😴 Snoozed ${r.snoozed} task${r.snoozed === 1 ? '' : 's'} for 1 hour` });
            setOpen(false);
          } catch (e) {
            toast.push({ kind: 'error', title: 'Could not snooze', message: errorMessage(e) });
          } finally {
            invalidateTasks(qc);
          }
        }}
      />
    </>
  );
}

/** 📌 Follow-up: quick options + custom. The original task is not changed. */
export function FollowUpDialog() {
  const ui = useUI();
  const t = ui.followUpFor;
  const qc = useQueryClient();
  const toast = useToast();
  const [due, setDue] = useState<DateSpec>({ mode: 'RELATIVE', amount: 3, workingDays: true });
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  if (!t) return null;
  const quick: Array<[string, DateSpec]> = [
    ['Tomorrow', { mode: 'RELATIVE', amount: 1, workingDays: false }],
    ['+2 days', { mode: 'RELATIVE', amount: 2, workingDays: false }],
    ['+3 working days', { mode: 'RELATIVE', amount: 3, workingDays: true }],
    ['+7 working days', { mode: 'RELATIVE', amount: 7, workingDays: true }],
  ];
  const save = async (spec: DateSpec) => {
    setError(null);
    try {
      const r = await post<{ followUp: { ref: string; dueDate: string } }>(`/api/tasks/${t.id}/follow-up`, { due: spec, title: title.trim() || null });
      toast.push({ kind: 'success', title: `📌 Follow-up ${r.followUp.ref} created`, message: title || `Follow-up: ${t.title}` });
      invalidateTasks(qc, t.id);
      ui.closeFollowUp();
      setTitle('');
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <Modal
      open
      onClose={ui.closeFollowUp}
      title="📌 Add follow-up"
      footer={
        <>
          <Button onClick={ui.closeFollowUp}>Cancel</Button>
          <Button variant="primary" onClick={() => save(due)}>
            Create follow-up
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small">
          Linked to <strong>{t.ref}</strong> — {t.title}
        </p>
        <div className="chip-row">
          {quick.map(([label, spec]) => (
            <Button key={label} size="sm" onClick={() => save(spec)}>
              {label}
            </Button>
          ))}
        </div>
        <Field label="Title (optional)" htmlFor="fu-title">
          <input id="fu-title" className="input" value={title} maxLength={300} placeholder={`Follow-up: ${t.title}`} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <DatePicker value={due} onChange={setDue} allowNone={false} label="Custom follow-up date" compact />
        {error && (
          <div className="inline-alert danger" role="alert">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
